import type { TFunction } from 'i18next';
import { formatDateLong, formatDateShort, formatGroupedNumber } from 'lib/dateFormat';
import { FEATURE_LABELS } from 'modules/entitlements/constants';
import { COIN_REDEMPTION_CAP_PCT } from 'modules/coins/constants';
import type { CoinLedgerEntry, CoinLedgerSource } from 'modules/coins/api';

// Dates and amounts are formatted by `lib/dateFormat`, not by `Intl` with a
// `uz-*` tag: runtimes missing Uzbek locale data fall back to CLDR root, which
// rendered months as "M07"/"M08" and grouped thousands with a comma.
export { formatDateLong };

/** Format a so'm amount with UZS grouping. Prices/charges are shown in so'm. */
export function formatSom(amount: number): string {
  return `${formatGroupedNumber(amount)} so'm`;
}

/** Format a raw coin count (1 coin = 1 so'm) without a currency suffix. */
export function formatCoinCount(amount: number): string {
  return formatGroupedNumber(amount);
}

/** Short human date, localized to the active language, e.g. "Jul 7, 2026". Returns "—" for empty input. */
export function formatDate(iso?: string): string {
  return formatDateShort(iso);
}

/**
 * One plan feature as a line: "Testlar — 5 tagacha", "AI tokenlar — 100 000 / oy",
 * "Tashkilot a'zolari — cheksiz", or just the name for an on/off feature.
 * Names are translated per feature key; an unknown key falls back to its English label.
 */
export function planFeatureLine(t: TFunction, featureKey: string, limit?: number | null): string {
  const fallback = FEATURE_LABELS[featureKey as keyof typeof FEATURE_LABELS] ?? featureKey.replace(/_/g, ' ');
  const name = t(`billing_feature_${featureKey}`, { defaultValue: fallback });
  if (limit === null) return t('feature_unlimited', { name });
  if (limit == null) return name;
  const value = formatGroupedNumber(limit);
  return featureKey.endsWith('_monthly')
    ? t('billing_feature_limit_monthly', { name, limit: value })
    : t('billing_feature_limit', { name, limit: value });
}

export type WalletCoverage =
  /** Coins pay the most the renewal accepts (COIN_REDEMPTION_CAP_PCT of it). */
  | { kind: 'max'; amount: number; pct: number }
  /** Coins pay part of the renewal. */
  | { kind: 'partial'; amount: number }
  /** Nothing renews, so coins only read as a discount at checkout. */
  | { kind: 'checkout' };

/** How much of the next renewal the wallet covers. Coins offset at most COIN_REDEMPTION_CAP_PCT of the price. */
export function walletCoverage(balance: number, renewalPrice: number | null): WalletCoverage {
  const price = renewalPrice ?? 0;
  if (price <= 0) return { kind: 'checkout' };
  const cap = Math.floor((COIN_REDEMPTION_CAP_PCT / 100) * price);
  const amount = Math.max(0, Math.min(balance, cap));
  if (cap > 0 && amount >= cap) return { kind: 'max', amount, pct: COIN_REDEMPTION_CAP_PCT };
  return { kind: 'partial', amount };
}

/** Icon by source — mapped to lucide icons in the row component. */
export const LEDGER_SOURCE_ICON: Record<CoinLedgerSource, 'gift' | 'tag' | 'card' | 'coins' | 'refund'> = {
  welcome: 'gift',
  referral: 'gift',
  promocode: 'tag',
  checkout: 'card',
  topup: 'card',
  refund: 'refund',
  admin: 'coins',
};

export interface LedgerDisplay {
  label: string;
  sub: string;
  positive: boolean;
  amountStr: string;
  balanceStr: string;
  iconKey: 'gift' | 'tag' | 'card' | 'coins' | 'refund';
}

/** Credits/releases/reversals add to the wallet; debits/reserves subtract from it. */
export function describeLedgerEntry(entry: CoinLedgerEntry, t: TFunction): LedgerDisplay {
  const positive = entry.type === 'credit' || entry.type === 'release' || entry.type === 'reversal';

  const sourceLabels: Record<CoinLedgerSource, string> = {
    welcome: t('coin_source_welcome', 'Welcome bonus'),
    referral: t('coin_source_referral', 'Referral reward'),
    promocode: t('coin_source_promocode', 'Promo code redeemed'),
    checkout: t('coin_source_checkout', 'Subscription payment'),
    topup: t('coin_source_topup', 'Coins purchased'),
    refund: t('coin_source_refund', 'Refund'),
    admin: t('coin_source_admin', 'Account adjustment'),
  };

  const label = sourceLabels[entry.source] ?? entry.source;
  const parts: string[] = [];
  if (entry.note) parts.push(entry.note);
  if (entry.createdAt) parts.push(formatDate(entry.createdAt));

  return {
    label,
    sub: parts.join(' · '),
    positive,
    iconKey: LEDGER_SOURCE_ICON[entry.source] ?? 'coins',
    amountStr: `${positive ? '+' : '−'}${formatCoinCount(Math.abs(entry.amount))} ${t('coins_unit', 'coins')}`,
    balanceStr: `${formatCoinCount(entry.balanceAfter)} ${t('coins_unit', 'coins')}`,
  };
}
